<template>
  <div class="w-full">
    <!-- Sidebar: ubicación + precio apilados (desktop marketplace) -->
    <form
      v-if="layout === 'sidebar'"
      @submit.prevent="handleSubmit"
      class="space-y-3 rounded-xl border border-gray-200 bg-white p-3 shadow-sm"
    >
      <div class="relative">
        <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
          {{ $t('colivingSearchBar.where') }}
        </label>
        <button
          type="button"
          class="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg border border-gray-200 bg-gray-50 text-sm font-medium text-gray-700 hover:border-teal-300 hover:bg-teal-50/40 transition-colors"
          @click="localShowLocationDropdown = !localShowLocationDropdown"
          data-location-button
        >
          <span class="truncate text-left">{{ getSelectedLocationsDisplay() }}</span>
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-400 flex-shrink-0"
            :class="{ 'rotate-180': localShowLocationDropdown }" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clip-rule="evenodd" />
          </svg>
        </button>
        <div
          v-if="localShowLocationDropdown"
          class="absolute top-full mt-1 left-0 right-0 z-[100] bg-white rounded-lg border border-gray-200 shadow-lg max-h-[min(50vh,320px)] overflow-y-auto p-3"
          data-location-dropdown-desktop
        >
          <div v-if="topCityOptions && topCityOptions.length" class="mb-3">
            <div class="flex flex-wrap gap-1.5">
              <button
                v-for="cityOpt in topCityOptions"
                :key="cityOpt.id"
                type="button"
                @click="handleTopCityChange(cityOpt.id)"
                :class="[
                  'px-3 py-1.5 rounded-full border text-xs font-medium transition-all',
                  selectedTopCity === cityOpt.id
                    ? 'border-teal-600 bg-teal-50 text-teal-800'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300',
                ]"
              >
                {{ cityOpt.flag }} {{ cityOpt.label }}
              </button>
            </div>
          </div>
          <div v-if="!topCityOptions || !topCityOptions.length" class="mb-2">
            <label class="flex items-center cursor-pointer py-1.5">
              <input type="checkbox" :checked="localSelectedLocations.length === 0" @change="handleSelectAllLocations"
                class="h-4 w-4 text-teal-700 border-gray-300 rounded focus:ring-teal-600" />
              <span class="ml-2 text-sm text-gray-700">{{ $t('colivingSearchBar.allCities') }}</span>
            </label>
          </div>
          <div :class="locationGridClass">
            <label v-for="location in visibleLocations" :key="location"
              class="flex items-center cursor-pointer py-1 hover:bg-gray-50 rounded px-1">
              <input type="checkbox" :value="location" :checked="localSelectedLocations.includes(location)"
                @change="handleLocationToggle(location)"
                class="h-4 w-4 text-teal-700 border-gray-300 rounded focus:ring-teal-600 flex-shrink-0" />
              <span class="ml-2 text-xs text-gray-700 truncate">{{ location }}</span>
            </label>
          </div>
          <div class="flex gap-2 pt-3 mt-2 border-t border-gray-100">
            <button type="button" @click="handleClearLocations"
              class="flex-1 px-2 py-2 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
              {{ $t('colivingSearchBar.clear') }}
            </button>
            <button type="button" @click="handleApplyLocationFilter"
              class="flex-1 px-2 py-2 text-xs font-medium text-teal-800 border border-teal-200 rounded-lg hover:bg-teal-50">
              {{ $t('colivingSearchBar.apply') }}
            </button>
          </div>
        </div>
      </div>

      <div v-if="!hidePrice">
        <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
          {{ $t('colivingSearchBar.maxPrice') }}
        </label>
        <select
          v-model.number="localMaxPrice"
          @change="handleMaxPriceChange"
          class="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm font-medium bg-white focus:border-teal-600 focus:ring-teal-500"
        >
          <option :value="null">{{ $t('colivingSearchBar.maxPrice') }}</option>
          <option v-for="opt in priceSelectOptions" :key="opt.slug" :value="opt.value">
            {{ opt.label }}
          </option>
        </select>
      </div>

      <button
        type="submit"
        :disabled="isLoading"
        class="w-full py-2.5 rounded-lg bg-teal-700 text-white text-sm font-semibold hover:bg-teal-800 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
      >
        <LoadingSpinner v-if="isLoading" size="small" color="text-white" :centered="false" />
        <span>{{ $t('colivingSearch.sidebar.search') }}</span>
      </button>
    </form>

    <form v-else @submit.prevent="handleSubmit"
      :class="[
        'flex-1 rounded-full flex items-center transition-all duration-300 mx-auto',
        prominentButton 
          ? 'bg-white shadow-[0_4px_20px_rgba(99,102,241,0.15)] ring-2 ring-indigo-500/30' 
          : 'bg-white',
        noBorder ? '' : 'border border-gray-200'
      ]"
      style="height: 52px;">
      <!-- Ubicación (Dropdown con checkboxes) -->
      <div :class="hidePrice ? 'flex-[2]' : 'flex-1'" class="min-w-0 h-full flex items-center relative">
        <div
          class="px-4 py-2 rounded-full hover:bg-gray-50 transition-colors cursor-pointer h-full flex items-center w-full"
          @click="localShowLocationDropdown = !localShowLocationDropdown" data-location-button>
          <div class="flex items-center justify-between w-full">
            <div class="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-400" viewBox="0 0 20 20"
                fill="currentColor">
                <path fill-rule="evenodd"
                  d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                  clip-rule="evenodd" />
              </svg>
              <span
                :class="(localSelectedLocations.length > 0 || selectedTopCity) ? 'ml-2 text-sm font-medium text-gray-700' : 'ml-2 text-sm font-medium text-gray-500'">
                {{ getSelectedLocationsDisplay() }}
              </span>
            </div>
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-400 flex-shrink-0 ml-1"
              :class="{ 'rotate-180': localShowLocationDropdown }" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd"
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                clip-rule="evenodd" />
            </svg>
          </div>
        </div>

        <!-- Dropdown de ubicaciones (Desktop) -->
        <div v-if="localShowLocationDropdown"
          class="hidden lg:block absolute top-full mt-2 left-0 bg-gray-50 rounded-lg border border-gray-200 shadow-lg"
          :style="locationDropdownStyle" data-location-dropdown-desktop>
          <div class="p-4 space-y-3">

            <!-- ── Selector Ciudad UK/US ──────────────────────────── -->
            <div v-if="topCityOptions && topCityOptions.length">
              <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{{ $t('colivingSearchBar.selectCity') }}</label>
              <div class="flex gap-2">
                <button
                  v-for="cityOpt in topCityOptions"
                  :key="cityOpt.id"
                  type="button"
                  @click="handleTopCityChange(cityOpt.id)"
                  :class="[
                    'flex items-center gap-1.5 px-4 py-2 rounded-full border text-sm font-medium transition-all flex-1 justify-center',
                    selectedTopCity === cityOpt.id
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700 shadow-sm'
                      : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400 hover:bg-gray-50'
                  ]"
                >
                  <span>{{ cityOpt.flag }}</span>
                  <span>{{ cityOpt.label }}</span>
                </button>
              </div>
            </div>

            <!-- ── Todas las ciudades (solo España) ──────────────── -->
            <div v-if="!topCityOptions || !topCityOptions.length">
              <label class="block text-sm font-medium text-gray-700 mb-2">{{ $t('colivingSearchBar.selectCities') }}</label>
              <label class="flex items-center cursor-pointer py-1.5 hover:bg-white rounded px-2 -mx-2">
                <input type="checkbox" :checked="localSelectedLocations.length === 0" @change="handleSelectAllLocations"
                  class="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500" />
                <span class="ml-2 text-sm font-medium text-gray-700">{{ $t('colivingSearchBar.allCities') }}</span>
              </label>
            </div>

            <!-- ── Barrios/Áreas ──────────────────────────────────── -->
            <div :class="(topCityOptions && topCityOptions.length) ? 'border-t border-gray-200 pt-3' : 'border-t border-gray-200 pt-3'">
              <label v-if="topCityOptions && topCityOptions.length" class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{{ $t('colivingSearchBar.selectArea') }}</label>
              <div :class="locationGridClass">
                <label v-for="location in visibleLocations" :key="location"
                  class="flex items-center cursor-pointer py-1.5 hover:bg-white rounded px-2 -mx-2">
                  <input type="checkbox" 
                    :value="location" 
                    :checked="localSelectedLocations.includes(location)"
                    @change="handleLocationToggle(location)"
                    class="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 flex-shrink-0" />
                  <span class="ml-2 text-sm text-gray-700 truncate">{{ location }}</span>
                </label>
              </div>
            </div>

            <!-- Botones de acción -->
            <div class="flex gap-2 pt-2 border-t border-gray-200">
              <button type="button" @click="handleClearLocations"
                class="flex-1 px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-full hover:bg-gray-50 transition-colors">
                {{ $t('colivingSearchBar.clear') }}
              </button>
              <button type="button" @click="handleApplyLocationFilter"
                class="flex-1 px-3 py-2 text-sm font-medium text-indigo-600 bg-white border border-indigo-200 rounded-full hover:bg-indigo-50 hover:shadow-md shadow-sm transition-all">
                {{ $t('colivingSearchBar.apply') }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Separador (solo si no está oculto el precio) -->
      <div v-if="!hidePrice" class="w-px h-6 bg-gray-200 mx-1 self-center"></div>

      <!-- Precio (hasta X / más de X) -->
      <div v-if="!hidePrice" class="flex-1 min-w-0 relative h-full flex items-center">
        <div class="px-4 py-2 rounded-full hover:bg-gray-50 transition-colors h-full flex items-center w-full">
          <select v-model.number="localMaxPrice"
            @change="handleMaxPriceChange"
            class="w-full border-0 p-0 text-sm font-medium bg-transparent focus:ring-0 focus:outline-none cursor-pointer"
            :class="hasPriceSelected ? 'text-gray-700' : 'text-gray-500'">
            <option :value="null">{{ $t('colivingSearchBar.maxPrice') }}</option>
            <option v-for="opt in priceSelectOptions" :key="opt.slug" :value="opt.value">
              {{ opt.label }}
            </option>
          </select>
        </div>
      </div>

      <!-- Botón de búsqueda -->
      <button type="submit" :disabled="isLoading"
        :class="[
          'rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center font-semibold text-sm mr-1.5 flex-shrink-0',
          prominentButton 
            ? 'bg-gray-600 text-white hover:bg-gray-700 shadow-lg hover:shadow-xl h-11 w-11 min-w-[44px]' 
            : 'bg-gray text-indigo-600 border border-indigo-200 hover:bg-gray-50 hover:shadow-md shadow-sm h-10 w-10 min-w-[40px]'
        ]"
        :aria-label="$t('colivingSearchBar.searchAriaLabel')">
        <LoadingSpinner v-if="isLoading" size="small" color="primary" :centered="false" />
        <svg v-else xmlns="http://www.w3.org/2000/svg" :class="prominentButton ? 'h-6 w-6' : 'h-5 w-5'" fill="none" viewBox="0 0 24 24"
            stroke="currentColor" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
      </button>
    </form>

    <!-- Panel de Ubicaciones (Mobile) -->
    <div v-if="localShowLocationDropdown" class="lg:hidden mt-2 bg-white border border-gray-200 rounded-lg shadow-lg" data-location-panel-mobile>
      <div class="px-3 py-4 bg-gray-50 space-y-4">

        <!-- ── Selector Ciudad UK/US (Mobile) ─────────────────── -->
        <div v-if="topCityOptions && topCityOptions.length">
          <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{{ $t('colivingSearchBar.selectCity') }}</label>
          <div class="flex gap-2">
            <button
              v-for="cityOpt in topCityOptions"
              :key="cityOpt.id"
              type="button"
              @click="handleTopCityChange(cityOpt.id)"
              :class="[
                'flex items-center gap-1.5 px-4 py-2.5 rounded-full border text-sm font-medium transition-all flex-1 justify-center',
                selectedTopCity === cityOpt.id
                  ? 'border-indigo-500 bg-indigo-50 text-indigo-700 shadow-sm'
                  : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400 hover:bg-gray-50'
              ]"
            >
              <span>{{ cityOpt.flag }}</span>
              <span>{{ cityOpt.label }}</span>
            </button>
          </div>
        </div>

        <!-- ── Todas las ciudades (solo España, Mobile) ──────── -->
        <div v-if="!topCityOptions || !topCityOptions.length">
          <label class="block text-sm font-medium text-gray-700 mb-2">{{ $t('colivingSearchBar.selectCities') }}</label>
          <label class="flex items-center cursor-pointer py-2 hover:bg-white rounded px-2 -mx-2">
            <input type="checkbox" :checked="localSelectedLocations.length === 0" @change="handleSelectAllLocations"
              class="h-5 w-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500" />
            <span class="ml-3 text-base font-medium text-gray-700">{{ $t('colivingSearchBar.allCities') }}</span>
          </label>
        </div>

        <!-- ── Barrios/Áreas (Mobile) ─────────────────────────── -->
        <div class="border-t border-gray-200 pt-3">
          <label v-if="topCityOptions && topCityOptions.length" class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{{ $t('colivingSearchBar.selectArea') }}</label>
          <div :class="locationGridClass">
            <label v-for="location in visibleLocations" :key="location"
              class="flex items-center cursor-pointer py-2 hover:bg-white rounded px-2 -mx-2">
              <input type="checkbox" 
                :value="location" 
                :checked="localSelectedLocations.includes(location)"
                @change="handleLocationToggle(location)"
                class="h-5 w-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500" />
              <span class="ml-2 text-sm text-gray-700">{{ location }}</span>
            </label>
          </div>
        </div>

        <!-- Botones de acción -->
        <div class="flex gap-2 pt-2">
          <button type="button" @click="handleClearLocations"
            class="flex-1 px-4 py-3 text-base font-medium text-gray-600 bg-white border border-gray-300 rounded-full hover:bg-gray-50 transition-colors">
            {{ $t('colivingSearchBar.clear') }}
          </button>
          <button type="button" @click="handleApplyLocationFilter"
            class="flex-1 px-4 py-3 text-base font-medium text-indigo-600 bg-white border border-indigo-200 rounded-full hover:bg-indigo-50 hover:shadow-md shadow-sm transition-all">
            {{ $t('colivingSearchBar.apply') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import LoadingSpinner from './LoadingSpinner.vue';
import { useRegion } from '@/composables/useRegion';
import { PRICE_SLUGS_BY_REGION } from '@/composables/useSearchFiltersSetup';

const { t } = useI18n();
const { regionId } = useRegion();

// Props
const props = defineProps({
  selectedLocations: {
    type: Array,
    default: () => []
  },
  maxPrice: {
    type: Number,
    default: null
  },
  availableLocations: {
    type: Array,
    required: true
  },
  isLoading: {
    type: Boolean,
    default: false
  },
  noBorder: {
    type: Boolean,
    default: false
  },
  hidePrice: {
    type: Boolean,
    default: false
  },
  prominentButton: {
    type: Boolean,
    default: false
  },
  // Opciones de ciudad de primer nivel (solo para UK/US)
  // Ej: [{ id: 'new-york', label: 'New York', flag: '🇺🇸' }, { id: 'london', label: 'London', flag: '🇬🇧' }]
  topCityOptions: {
    type: Array,
    default: null
  },
  selectedTopCity: {
    type: String,
    default: null
  },
  layout: {
    type: String,
    default: 'default',
    validator: (v) => ['default', 'sidebar'].includes(v),
  },
});

// Emits
const emit = defineEmits(['update:selectedLocations', 'update:maxPrice', 'update:selectedTopCity', 'submit', 'apply-location-filter']);

// Estado local
const localShowLocationDropdown = ref(false);

// Computed bidireccionales para evitar ciclos infinitos
const localSelectedLocations = computed({
  get: () => props.selectedLocations,
  set: (value) => emit('update:selectedLocations', value)
});

const localMaxPrice = computed({
  get: () => props.maxPrice,
  set: (value) => emit('update:maxPrice', value)
});

const visibleLocations = computed(() =>
  props.availableLocations.filter((location) => location !== 'San Sebastián')
);

// Grid adaptativo: sin scroll interno, más columnas si hay muchas ubicaciones
const locationGridClass = computed(() => {
  const count = visibleLocations.value.length;
  if (count > 20) return 'grid grid-cols-3 gap-x-2 gap-y-0.5';
  if (count > 12) return 'grid grid-cols-3 gap-x-2 gap-y-1';
  return 'grid grid-cols-2 gap-x-2 gap-y-1';
});

const locationDropdownStyle = computed(() => ({
  zIndex: 99999,
  minWidth: visibleLocations.value.length > 12 ? '480px' : '360px',
}));

// ── Baremos de precio (incluye «más de X» con valor negativo) ────────────────
const priceSelectOptions = computed(() => {
  const region = regionId.value || 'es';
  return PRICE_SLUGS_BY_REGION[region] || PRICE_SLUGS_BY_REGION.es;
});

const hasPriceSelected = computed(() =>
  props.maxPrice != null && props.maxPrice !== 0,
);

// Texto que muestra la barra de búsqueda
const getSelectedLocationsDisplay = () => {
  // Modo ciudad UK/US
  if (props.topCityOptions && props.topCityOptions.length) {
    const cityLabel = props.topCityOptions.find(c => c.id === props.selectedTopCity)?.label || ''
    if (!cityLabel) return t('colivingSearchBar.where')
    if (localSelectedLocations.value.length === 0) return cityLabel
    if (localSelectedLocations.value.length === 1) return `${cityLabel} · ${localSelectedLocations.value[0]}`
    return `${cityLabel} · ${localSelectedLocations.value[0]} +${localSelectedLocations.value.length - 1}`
  }
  // Modo España
  if (localSelectedLocations.value.length === 0) {
    return t('colivingSearchBar.where');
  } else if (localSelectedLocations.value.length === 1) {
    return localSelectedLocations.value[0];
  } else if (localSelectedLocations.value.length === 2) {
    return localSelectedLocations.value.join(', ');
  } else {
    return `${localSelectedLocations.value[0]}, ${localSelectedLocations.value[1]} +${localSelectedLocations.value.length - 2}`;
  }
};

const handleTopCityChange = (cityId) => {
  if (cityId === props.selectedTopCity) return
  emit('update:selectedLocations', []);
  emit('update:selectedTopCity', cityId);
};

const handleLocationToggle = (location) => {
  const currentLocations = [...localSelectedLocations.value];
  const index = currentLocations.indexOf(location);
  
  if (index > -1) {
    currentLocations.splice(index, 1);
  } else {
    currentLocations.push(location);
  }
  
  emit('update:selectedLocations', currentLocations);
};

const handleSelectAllLocations = () => {
  emit('update:selectedLocations', []);
};

const handleClearLocations = () => {
  emit('update:selectedLocations', []);
  localShowLocationDropdown.value = false;
};

const handleApplyLocationFilter = () => {
  localShowLocationDropdown.value = false;
  emit('apply-location-filter');
};

const handleSubmit = () => {
  emit('submit');
};

const handleMaxPriceChange = () => {
  emit('submit');
};

// Cerrar el dropdown al hacer clic fuera
const handleClickOutside = (event) => {
  if (!localShowLocationDropdown.value) return;
  
  const locationDropdownDesktop = document.querySelector('[data-location-dropdown-desktop]');
  const locationDropdownMobile = document.querySelector('[data-location-panel-mobile]');
  const locationButton = event.target.closest('[data-location-button]');
  
  if (!locationButton && 
      (!locationDropdownDesktop || !locationDropdownDesktop.contains(event.target)) &&
      (!locationDropdownMobile || !locationDropdownMobile.contains(event.target))) {
    localShowLocationDropdown.value = false;
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped>
/* Estilos específicos si fueran necesarios */
</style>

